import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';

import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/compat/firestore';
import { Observable, Subscription } from 'rxjs';

import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { SelectionModel } from '@angular/cdk/collections';
import { ToastrService } from 'ngx-toastr';

import { Shelter } from '../firestore-models/shelter';
import { ProjectLanguage } from '../firestore-models/project-language';
import { ApiShelter, ApiSetSheltersRequest } from '../api-client-module';

import { ApiClientService } from '../api-client.service';
import { ConfigurationService } from '../configuration.service';
import { SpinnerService } from '../services/spinner.service';
import { AppConst } from '../app-const';

import { ShelterDialogComponent } from './shelter-dialog/shelter-dialog.component';
import { MessageDialogComponent } from '../common/message-dialog/message-dialog.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-shelters',
  templateUrl: './shelters.component.html',
  styleUrls: ['./shelters.component.css'],
  standalone: false,
})
export class SheltersComponent implements OnInit {

  //Shelterコレクション
  private sheltersCollection!: AngularFirestoreCollection<Shelter>;
  sheltersObservable!: Observable<Shelter[]>;
  sheltersSubscribe!: Subscription;
  //建物情報に紐付くプロジェクトID
  projectId: string;
  //建物情報に紐付くプロジェクトの言語情報
  projectLanguages!: Array<ProjectLanguage>;

  //建物情報テーブル
  displayedColumns: string[] = ['position', 'edit', 'select', 'shelter_id', 'name', 'date'];
  dataSource = new MatTableDataSource<Shelter>();
  selection = new SelectionModel<Shelter>(true, []);

  projectBaseLang: string = 'ja';

  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort!: MatSort;

  constructor(
    public dialog: MatDialog,
    private angularFirestore: AngularFirestore,
    private apiClientService: ApiClientService,
    private spinnerService: SpinnerService,
    private toastr: ToastrService,
    private configurationService: ConfigurationService,
    private translate: TranslateService
  ) {
    this.projectId = this.configurationService.getProjectId();
  }

  ngOnInit(): void {
    this.projectLanguages = this.configurationService.getProject().languages!;
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.sheltersCollection = this.angularFirestore.collection<Shelter>(
      AppConst.FIRESTORE_SHELTERS, ref => ref.where('project_id', '==', this.projectId).orderBy('shelter_id', 'asc'));
    this.sheltersObservable = this.sheltersCollection.valueChanges({ idField: 'id' });
    this.sheltersSubscribe = this.sheltersObservable.subscribe(shelters => {
      this.dataSource.data = this.addFilterValue(shelters);
    });
    this.projectBaseLang = this.configurationService.getProject().project_base_lang;
  }

  ngOnDestroy(): void {
    this.sheltersSubscribe.unsubscribe();
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  masterToggle() {
    this.isAllSelected() ?
      this.selection.clear() :
      this.dataSource.data.forEach(row => this.selection.select(row));
  }

  checkboxLabel(row?: Shelter): string {
    if (!row) {
      return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'}`;
  }

  //編集ダイアログ
  openEditDialog(shelter: Shelter): void {
    let apiShelter: ApiShelter = {
      shelter_id: shelter.shelter_id,
      shelter_names: shelter.shelter_names,
      latitude: shelter.shelter_geopoint ? shelter.shelter_geopoint.latitude : AppConst.LATITUDE_INITIAL_VALUE,
      longitude: shelter.shelter_geopoint ? shelter.shelter_geopoint.longitude : AppConst.LONGITUDE_INITIAL_VALUE,
      show_marker: shelter.show_marker,
      project_id: shelter.project_id,
      enabled: shelter.enabled,
    };
    const dialogRef = this.dialog.open(ShelterDialogComponent, {
      width: '50%',
      disableClose: true,
      data: { change_type: AppConst.DATA_CHANGE_TYPE_UPDATE, shelter: apiShelter }
    });
  }

  //追加ダイアログ
  openAddDialog(): void {
    const dialogRef = this.dialog.open(ShelterDialogComponent, {
      width: '50%',
      disableClose: true,
      data: { change_type: AppConst.DATA_CHANGE_TYPE_ADD, shelter: null }
    });
  }

  //削除
  deleteData(): void {
    const dialogRef = this.dialog.open(MessageDialogComponent, {
      width: '420px',
      height: '180px',
      disableClose: true,
      data: { message: this.translate.instant('選択されたデータを削除します。よろしいですか？') }
    });

    dialogRef.afterClosed().subscribe(result => {
      let selectedShelters: Array<ApiShelter> = new Array<ApiShelter>();
      for (const row of this.dataSource.data) {
        if (this.selection.isSelected(row)) {
          const selectedShelter: ApiShelter = {
            shelter_id: row.shelter_id,
            shelter_names: row.shelter_names,
            latitude: row.shelter_geopoint.latitude,
            longitude: row.shelter_geopoint.longitude,
            show_marker: row.show_marker,
            project_id: row.project_id,
            enabled: row.enabled,
          };
          selectedShelters.push(selectedShelter);
        }
      }
      if (!!result) {
        let request: ApiSetSheltersRequest = {
          access_token: '',
          change_type: AppConst.DATA_CHANGE_TYPE_DELETE,
          shelters: selectedShelters
        }
        this.spinnerService.openSpinner();
        this.apiClientService.setShelters(request).then(
          response => {
            if (response!.ret === AppConst.API_RET_SUCCESS) {
              this.toastr.success(this.translate.instant('削除しました。'));
            } else {
              this.toastr.error(this.translate.instant('削除できませんでした。'));
            }
          }
        ).catch(
          error => {
            console.log(error);
            this.toastr.error(this.translate.instant('削除できませんでした。'));
          }
        ).finally(
          () => {
            this.spinnerService.closeSpinner();
            this.selection.clear();
          }
        );
      }
    });
  }

  sortData(sort: Sort) {
    const data = this.dataSource.data.slice();
    if (!sort.active || sort.direction === '') {
      this.dataSource.data = data;
      return;
    }
    const compare = (a: number | string, b: number | string, isAsc: boolean) => {
      return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
    }

    this.dataSource.data = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'shelter_id':
          return compare(a.shelter_id, b.shelter_id, isAsc);
        case 'name':
          const aName = a.shelter_names.filter((d) => { return d.lang_code == this.projectLanguages[0].lang_code })[0].name;
          const bName = b.shelter_names.filter((d) => { return d.lang_code == this.projectLanguages[0].lang_code })[0].name;
          return compare(aName, bName, isAsc);
        case 'date':
          return compare(a.updated.toMillis(), b.updated.toMillis(), isAsc);
        default:
          return 0;
      }
    });
  }

  addFilterValue(docs: Array<any>): Array<any> {
    for (const doc of docs) {
      let filterValue = '';
      for (const value of doc.shelter_names) {
        filterValue += value.name + ' '
      }
      doc['filterValue'] = filterValue;
    }
    return docs;
  }

  applyFilter(event: Event) {
    this.selection.clear();
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

}
